import type { NoteCategory, NotePriority, NoteStatus } from "../types/notes-api.types";

export const NOTES_API_ENDPOINTS = {
  NOTES: "/internal/operations/notes",
  noteById: (id: string) => `/internal/operations/notes/${encodeURIComponent(id)}`,
  noteAttachment: (id: string) => `/internal/operations/notes/${encodeURIComponent(id)}/attachment`,
} as const;

// Atoms only: `/auth/me` projects the RBAC-effective permissions, and that projection emits
// `read`/`write`/`delete` exclusively — a composite (`operations:notes:admin`) never appears in
// `scopes[]`, so checking one is always false. The former admin scope maps to its atom: the backend
// expands `admin` to read+write+delete, and `delete` is the one that separates a moderator from an author.
export const NOTES_WRITE_SCOPE = "operations:notes:write" as const;
export const NOTES_MODERATE_SCOPE = "operations:notes:delete" as const;

export const NOTES_CREATE_SCOPES = [NOTES_WRITE_SCOPE] as const;

export const NOTES_PAGE_SIZE = 12;

export const NOTE_CATEGORY_LABELS: Record<NoteCategory, string> = {
  WORK_PERMIT: "Permiso de trabajo",
  SCHEDULED_MAINTENANCE: "Mantenimiento programado",
  INCIDENT: "Incidente",
  GENERAL: "General",
  REMINDER: "Recordatorio",
  URGENT_NOTICE: "Aviso urgente",
  PROCESS_CHANGE: "Cambio de proceso",
  CLIENT_ISSUE: "Incidencia con cliente",
  TRAINING: "Capacitación",
  MEETING: "Reunión",
};

export const NOTE_CATEGORY_BADGE_CLASS: Record<NoteCategory, string> = {
  WORK_PERMIT: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400",
  SCHEDULED_MAINTENANCE: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400",
  INCIDENT: "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400",
  GENERAL: "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300",
  REMINDER: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400",
  URGENT_NOTICE: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
  PROCESS_CHANGE: "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400",
  CLIENT_ISSUE: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
  TRAINING: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
  MEETING: "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400",
};

export const NOTE_PRIORITY_LABELS: Record<NotePriority, string> = {
  LOW: "Baja",
  MEDIUM: "Media",
  HIGH: "Alta",
  URGENT: "Urgente",
};

export const NOTE_STATUS_LABELS: Record<NoteStatus, string> = {
  ACTIVE: "Activa",
  INACTIVE: "Inactiva",
};

export const NOTE_CATEGORY_OPTIONS: { value: NoteCategory; label: string }[] = Object.entries(
  NOTE_CATEGORY_LABELS,
).map(([value, label]) => ({
  value: value as NoteCategory,
  label,
}));

export const NOTE_PRIORITY_OPTIONS: { value: NotePriority; label: string }[] = Object.entries(
  NOTE_PRIORITY_LABELS,
).map(([value, label]) => ({
  value: value as NotePriority,
  label,
}));

export const NOTE_STATUS_OPTIONS: { value: NoteStatus; label: string }[] = Object.entries(
  NOTE_STATUS_LABELS,
).map(([value, label]) => ({
  value: value as NoteStatus,
  label,
}));
